/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Typography2, typography2 } from '@coursera/cds-core';

import TogglableContent from 'bundles/page/components/TogglableContent';
import ACECreditRecommendationInfo from 'bundles/unified-description-page-common/components/modules/credits/ACECreditRecommendationInfo';
import EligibleDegreesInfo from 'bundles/unified-description-page-common/components/modules/credits/EligibleDegreesInfo';
import { EligibleDegreesModule } from 'bundles/unified-description-page-common/components/modules/credits/EligibleDegreesModule';
import ToggleButtonForStreamlinedMWebEligibleDegrees from 'bundles/unified-description-page-common/components/shared/ToggleButtonForStreamlinedMWebEligibleDegrees';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import getEligibleDegreesDescription from 'bundles/unified-description-page-common/utils/getEligibleDegreesDescription';
import getEligibleDegreesTitle from 'bundles/unified-description-page-common/utils/getEligibleDegreesTitle';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import isForCreditNonDegree from 'bundles/unified-description-page-common/utils/isForCreditNonDegree';
import { useMWebStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

import _t from 'i18n!nls/xdp';

type PageData = ConsumerCoursePageFieldsFragment | ConsumerSpecializationPageFieldsFragment;

type Props = {
  pageData: Pick<PageData, 'degrees' | 'partners' | 'slug'> & {
    productTags?: ConsumerSpecializationPageFieldsFragment['productTags'];
    productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
  };
  config?: {
    disableCta?: boolean;
  };
};

export const getProductVariant = (pageData: {
  primarySpecialization?: ConsumerCoursePageFieldsFragment['primarySpecialization'];
  productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
}) => {
  if (pageData.productVariant) {
    return pageData.productVariant;
  } else if (pageData.primarySpecialization?.productVariant) {
    return pageData.primarySpecialization.productVariant;
  }
  return undefined;
};

const styles = {
  mWebToggleableContent: css`
    .toggle-button-wrapper span {
      ${typography2.actionPrimary}
    }

    .ace-section {
      .content {
        width: 100%;
      }

      margin-top: calc(var(--cds-spacing-100) * -1);
      padding-bottom: var(--cds-spacing-100);

      .content-inner {
        margin-top: var(--cds-spacing-300);
      }
    }
  `,
};

const ConsumerEligibleDegreesModuleRenderer = ({ pageData, config }: Props) => {
  const hasDegrees = pageData.degrees && pageData.degrees.length > 0;
  const hasACELink = pageData.productTags?.linkToACECreditRecommendation;
  const isMWebStreamlinedXDP = useMWebStreamlinedXDP();

  if (!hasDegrees && !hasACELink) {
    return null;
  }

  const eligibleDegreesTitle = hasDegrees
    ? getEligibleDegreesTitle(pageData.degrees ?? [])
    : _t('Get a head start on your degree');

  const isFCND = isForCreditNonDegree(pageData?.slug, pageData?.partners?.[0]?.shortName || '');
  const variantTitle = getVariantTitle(pageData.productVariant);

  const { description: eligibleDegreesDescription, disclaimer: eligibleDegreesDisclaimer } =
    getEligibleDegreesDescription(
      variantTitle,
      getProductVariant(pageData),
      pageData.degrees?.[0]?.upsellPartners?.[0]?.name,
      pageData.degrees?.[0]?.upsellType,
      isFCND,
      pageData?.partners?.[0]?.name
    );

  return (
    <EligibleDegreesModule
      title={
        <Typography2 variant="titleSmall" component="h2" color="body">
          {eligibleDegreesTitle}
        </Typography2>
      }
    >
      {hasDegrees && (
        <EligibleDegreesInfo
          modalTitle={eligibleDegreesTitle}
          description={eligibleDegreesDescription}
          disclaimer={eligibleDegreesDisclaimer}
          degrees={pageData.degrees}
          disableCta={config?.disableCta}
        />
      )}
      {hasACELink &&
        (isMWebStreamlinedXDP ? (
          <div css={styles.mWebToggleableContent}>
            <TogglableContent
              childrenWrapperTag="div"
              className="ace-section"
              collapsedHeight={0}
              disableCollapseScroll
              renderToggleButtonContentFunction={ToggleButtonForStreamlinedMWebEligibleDegrees}
              toggleButtonType="button"
              useSmartCollapse
            >
              <ACECreditRecommendationInfo
                variantTitle={variantTitle}
                // @ts-expect-error URL: string
                aceLink={pageData.productTags?.linkToACECreditRecommendation}
                disableCta={config?.disableCta}
              />
            </TogglableContent>
          </div>
        ) : (
          <ACECreditRecommendationInfo
            variantTitle={variantTitle}
            // @ts-expect-error URL: string
            aceLink={pageData.productTags?.linkToACECreditRecommendation}
            disableCta={config?.disableCta}
          />
        ))}
    </EligibleDegreesModule>
  );
};

export default ConsumerEligibleDegreesModuleRenderer;
